import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, FileText, DollarSign, Users, Eye, Send, ArrowLeft, ArrowRight, Sparkles, CheckCircle, AlertTriangle, Bot } from 'lucide-react';
import { generateEngagementAndInvoice, previewEngagementLetter, listTemplates } from '../../services/esignatureService';
import axios from 'axios';
import DOMPurify from 'dompurify';

const EngagementLetterModal = ({ isOpen, onClose, intake, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [polishLoading, setPolishLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSummary, setPreviewSummary] = useState(null);
  const [aiReview, setAiReview] = useState(null);
  const [step, setStep] = useState(1); // 1: Fee Structure, 2: Scope, 3: Case Team, 4: Signers, 5: Preview
  const [assignableUsers, setAssignableUsers] = useState({ attorneys: [], staff: [], all: [] });

  const [formData, setFormData] = useState({
    intake_id: intake?.id || '',
    letter_type: 'standard',
    practice_area: intake?.practice_area || '',
    client_salutation: 'Ms.',
    fee_type: 'prepaid',
    hourly_rate: 300.00,
    flat_fee_amount: null,
    contingency_percentage: null,
    retainer_amount: 6000.00,
    prepaid_hours: 20,
    prepaid_rate: 300.00,
    scope_of_representation: '',
    fee_agreement: 'upon receipt', // Payment terms
    termination_clause: null,
    additional_terms: null,
    initial_payment: null,
    monthly_payment: null,
    payment_day: 15,
    exclusions: null,
    invoice_due_days: 30,
    invoice_notes: 'Non-Refundable Advance Fee Retainer',
    invoice_terms: null,
    signers: [
      {
        signer_name: `${intake?.first_name || ''} ${intake?.last_name || ''}`.trim(),
        signer_email: intake?.email || '',
        signer_type: 'client',
        signing_order: 1
      }
    ],
    email_subject: `Please sign your engagement letter - ${intake?.practice_area || 'Legal Services'}`,
    email_message: 'Thank you for choosing our firm. Please review and sign the attached engagement letter. An invoice for the retainer fee will be included.',
    // Case Team Assignment
    responsible_attorney: '',
    originating_attorney: '',
    responsible_staff: ''
  });

  // Load assignable users for case team selection
  useEffect(() => {
    const loadAssignableUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.REACT_APP_API_URL || '/api';
        const response = await axios.get(`${API_URL}/cases/assignable-users/list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAssignableUsers(response.data);
      } catch (err) {
        console.error('Failed to load assignable users:', err);
      }
    };
    if (isOpen) {
      loadAssignableUsers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Reset to step 1 and clear preview
      setStep(1);
      setPreviewHtml(null);
      setPreviewSummary(null);
      setAiReview(null);

      // Update intake-specific data
      setFormData(prev => ({
        ...prev,
        intake_id: intake?.id || '',
        practice_area: intake?.practice_area || '',
        scope_of_representation: intake?.practice_area ?
          `all matters concerning ${intake.practice_area}` : '',
        signers: [
          {
            signer_name: `${intake?.first_name || ''} ${intake?.last_name || ''}`.trim(),
            signer_email: intake?.email || '',
            signer_type: 'client',
            signing_order: 1
          }
        ],
        email_subject: `Please sign your engagement letter - ${intake?.practice_area || 'Legal Services'}`
      }));
    }
  }, [isOpen, intake]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSignerChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      signers: prev.signers.map((signer, i) =>
        i === index ? { ...signer, [field]: value } : signer
      )
    }));
  };

  const addSigner = () => {
    setFormData(prev => ({
      ...prev,
      signers: [...prev.signers, {
        signer_name: '',
        signer_email: '',
        signer_type: 'other',
        signing_order: prev.signers.length + 1
      }]
    }));
  };

  const removeSigner = (index) => {
    setFormData(prev => ({
      ...prev,
      signers: prev.signers.filter((_, i) => i !== index)
    }));
  };

  // Auto-calculate retainer amount for prepaid hours
  useEffect(() => {
    if (formData.fee_type === 'prepaid' && formData.prepaid_hours && formData.prepaid_rate) {
      const total = formData.prepaid_hours * formData.prepaid_rate;
      setFormData(prev => ({ ...prev, retainer_amount: total }));
    }
  }, [formData.fee_type, formData.prepaid_hours, formData.prepaid_rate]);

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const response = await previewEngagementLetter(formData);
      console.log('Preview response:', response.data);
      console.log('Preview HTML length:', response.data.preview_html?.length);
      setPreviewHtml(response.data.preview_html);
      setPreviewSummary(response.data.summary);
      setAiReview(response.data.ai_review);
      setStep(5);
    } catch (err) {
      console.error('Error previewing engagement letter:', err);
      alert('Failed to generate preview. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePolishScope = async () => {
    if (!formData.scope_of_representation.trim()) {
      alert('Please enter a scope of representation first.');
      return;
    }
    setPolishLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      const response = await axios.post(`${API_URL}/astraea-internal/polish-text`, {
        text: formData.scope_of_representation,
        context: 'engagement letter scope of representation for a law firm'
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.polished_text) {
        setFormData(prev => ({ ...prev, scope_of_representation: response.data.polished_text }));
      }
    } catch (err) {
      console.error('Error polishing text:', err);
      alert('Failed to polish text. Please try again.');
    } finally {
      setPolishLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await generateEngagementAndInvoice(formData);
      onSuccess(response.data);
      onClose();
    } catch (err) {
      console.error('Error generating engagement letter:', err);
      alert('Failed to generate engagement letter and invoice');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const steps = [
    { num: 1, label: 'Fee Structure' },
    { num: 2, label: 'Matter Details' },
    { num: 3, label: 'Case Team' },
    { num: 4, label: 'Signers' },
    { num: 5, label: 'Preview & Send' }
  ];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-virgo-navy-600 flex items-center justify-between bg-gradient-to-r from-slate-900/50 to-blue-900/50">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-cyan-400" />
            <div>
              <h2 className="text-xl font-semibold text-white">Generate Engagement Letter</h2>
              <p className="text-sm text-cyan-300">
                {intake?.first_name} {intake?.last_name} - {intake?.practice_area}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-virgo-teal-400 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-3 bg-virgo-navy-800/50 border-b border-virgo-navy-600">
          <div className="flex items-center justify-center gap-2">
            {steps.map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                    step >= s.num ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white' : 'bg-virgo-navy-600 text-virgo-teal-500'
                  }`}>
                    {s.num}
                  </div>
                  <span className={`text-sm font-medium hidden sm:inline ${step >= s.num ? 'text-cyan-400' : 'text-virgo-teal-500'}`}>
                    {s.label}
                  </span>
                </div>
                {idx < steps.length - 1 && <div className="w-8 h-0.5 bg-virgo-navy-600" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Fee Structure */}
          {step === 1 && (
            <div className="space-y-6 max-w-2xl mx-auto">
              <div className="bg-virgo-navy-800/50 rounded-lg p-4 border border-cyan-500/30">
                <h3 className="text-lg font-medium text-white mb-2">Standard Fee Structure</h3>
                <p className="text-sm text-virgo-teal-400">
                  Our standard engagement letter includes all required legal language, billing terms,
                  and client responsibilities. Simply specify the fee type and amounts below.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Fee Type</label>
                <select
                  value={formData.fee_type}
                  onChange={(e) => handleInputChange('fee_type', e.target.value)}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                >
                  <option value="prepaid">Prepaid Hours (Non-Refundable Advance Fee)</option>
                  <option value="hourly">Standard Hourly</option>
                  <option value="flat">Flat Fee</option>
                  <option value="contingency">Contingency</option>
                  <option value="hybrid_contingency">Hybrid Contingency (Upfront + % of Recovery)</option>
                </select>
              </div>

              {formData.fee_type === 'prepaid' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Prepaid Hours</label>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      value={formData.prepaid_hours || ''}
                      onChange={(e) => handleInputChange('prepaid_hours', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Rate ($/hour)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.prepaid_rate || ''}
                      onChange={(e) => handleInputChange('prepaid_rate', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="300.00"
                    />
                  </div>
                </div>
              )}

              {formData.fee_type === 'hourly' && (
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Hourly Rate ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.hourly_rate || ''}
                    onChange={(e) => handleInputChange('hourly_rate', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                </div>
              )}

              {formData.fee_type === 'flat' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Total Flat Fee ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.flat_fee_amount || ''}
                        onChange={(e) => handleInputChange('flat_fee_amount', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        placeholder="10000.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Initial Payment ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.initial_payment || ''}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          handleInputChange('initial_payment', val);
                          handleInputChange('retainer_amount', val);
                        }}
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        placeholder="3500.00"
                      />
                      <p className="mt-1 text-xs text-virgo-teal-500">This will be the invoice amount</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Monthly Payment ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.monthly_payment || ''}
                        onChange={(e) => handleInputChange('monthly_payment', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        placeholder="1000.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Payment Day of Month</label>
                      <input
                        type="number"
                        min="1"
                        max="28"
                        value={formData.payment_day || 15}
                        onChange={(e) => handleInputChange('payment_day', parseInt(e.target.value))}
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Exclusions (services NOT included)</label>
                    <input
                      type="text"
                      value={formData.exclusions || ''}
                      onChange={(e) => handleInputChange('exclusions', e.target.value)}
                      className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 placeholder-virgo-teal-600"
                      placeholder="e.g., appeals, depositions, expert witnesses"
                    />
                    <p className="mt-1 text-xs text-virgo-teal-500">Comma-separated list of excluded services</p>
                  </div>
                  {formData.flat_fee_amount && formData.initial_payment && (
                    <div className="bg-virgo-navy-800/80 rounded-lg p-3 border border-cyan-500/20">
                      <p className="text-sm text-cyan-300">
                        Total: <strong>${formData.flat_fee_amount?.toLocaleString()}</strong> |
                        Initial: <strong>${formData.initial_payment?.toLocaleString()}</strong> |
                        Remaining: <strong>${(formData.flat_fee_amount - formData.initial_payment)?.toLocaleString()}</strong>
                        {formData.monthly_payment > 0 && ` (${Math.ceil((formData.flat_fee_amount - formData.initial_payment) / formData.monthly_payment)} months)`}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {formData.fee_type === 'contingency' && (
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Contingency Percentage (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.contingency_percentage || ''}
                    onChange={(e) => handleInputChange('contingency_percentage', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                </div>
              )}

              {formData.fee_type === 'hybrid_contingency' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Upfront Fee ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.retainer_amount || ''}
                        onChange={(e) => handleInputChange('retainer_amount', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        placeholder="5000.00"
                      />
                      <p className="mt-1 text-xs text-virgo-teal-500">Non-refundable upfront fee</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Contingency Percentage (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={formData.contingency_percentage || ''}
                        onChange={(e) => handleInputChange('contingency_percentage', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        placeholder="33.33"
                      />
                      <p className="mt-1 text-xs text-virgo-teal-500">Percentage of gross recovery</p>
                    </div>
                  </div>
                  {formData.retainer_amount && formData.contingency_percentage && (
                    <div className="bg-virgo-navy-800/80 rounded-lg p-3 border border-cyan-500/20">
                      <p className="text-sm text-cyan-300">
                        Upfront: <strong>${formData.retainer_amount?.toLocaleString()}</strong> + <strong>{formData.contingency_percentage}%</strong> of gross recovery
                      </p>
                    </div>
                  )}
                </div>
              )}

              {formData.fee_type !== 'flat' && formData.fee_type !== 'hybrid_contingency' && (
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                    {formData.fee_type === 'prepaid' ? 'Total Prepaid Amount' : 'Retainer Amount'} ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.retainer_amount || ''}
                    onChange={(e) => handleInputChange('retainer_amount', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    readOnly={formData.fee_type === 'prepaid'}
                  />
                  {formData.fee_type === 'prepaid' && (
                    <p className="mt-1 text-xs text-green-400">
                      = {formData.prepaid_hours || 0} hours x ${formData.prepaid_rate || 0}/hr
                    </p>
                  )}
                </div>
              )}

              {formData.fee_type !== 'flat' && (
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Payment Terms</label>
                  <select
                    value={formData.fee_agreement}
                    onChange={(e) => handleInputChange('fee_agreement', e.target.value)}
                    className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  >
                    <option value="upon receipt">Due upon receipt</option>
                    <option value="half immediately and the remainder within a week">Half immediately, remainder within a week</option>
                    <option value="within 7 days">Due within 7 days</option>
                    <option value="within 14 days">Due within 14 days</option>
                    <option value="within 30 days">Due within 30 days</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Matter Details */}
          {step === 2 && (
            <div className="space-y-6 max-w-2xl mx-auto">
              <div className="bg-virgo-navy-800/50 rounded-lg p-4 border border-blue-500/30">
                <h3 className="text-lg font-medium text-white mb-2">Matter Description</h3>
                <p className="text-sm text-virgo-teal-400">
                  Describe what you will be representing the client for. This appears in Section 1
                  (Scope of Representation) of the engagement letter.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                    Client Salutation
                  </label>
                  <select
                    value={formData.client_salutation}
                    onChange={(e) => handleInputChange('client_salutation', e.target.value)}
                    className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  >
                    <option value="Mr.">Mr.</option>
                    <option value="Ms.">Ms.</option>
                    <option value="Mrs.">Mrs.</option>
                    <option value="Dr.">Dr.</option>
                    <option value="Prof.">Prof.</option>
                  </select>
                  <p className="mt-1 text-xs text-virgo-teal-500">
                    Letter will say "Dear {formData.client_salutation} {intake?.last_name}"
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                    Practice Area
                  </label>
                  <input
                    type="text"
                    value={formData.practice_area}
                    onChange={(e) => handleInputChange('practice_area', e.target.value)}
                    className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder="e.g., Probate, Estate Planning"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-virgo-teal-400">
                    Scope of Representation *
                  </label>
                  <button
                    onClick={handlePolishScope}
                    disabled={polishLoading || !formData.scope_of_representation.trim()}
                    className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-cyan-300 bg-cyan-500/20 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {polishLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-cyan-300"></div>
                        Polishing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3" />
                        AI Polish
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  value={formData.scope_of_representation}
                  onChange={(e) => handleInputChange('scope_of_representation', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="e.g., all matters concerning the Probate of Mr. Richard Dull's Estate"
                  required
                />
                <p className="mt-1 text-xs text-virgo-teal-500">
                  This will complete the sentence: "We will represent you regarding..."
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Case Team Assignment */}
          {step === 3 && (
            <div className="space-y-6 max-w-2xl mx-auto">
              <div className="bg-virgo-navy-800/50 rounded-lg p-4 border border-cyan-500/30">
                <h3 className="text-lg font-medium text-white mb-2">Case Team Assignment</h3>
                <p className="text-sm text-virgo-teal-400">
                  Assign the attorneys and staff who will work on this case. These roles will be set
                  when the engagement letter is signed and the case becomes active.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                    Responsible Attorney
                  </label>
                  <select
                    value={formData.responsible_attorney}
                    onChange={(e) => handleInputChange('responsible_attorney', e.target.value)}
                    className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  >
                    <option value="">— Select Attorney —</option>
                    {assignableUsers.attorneys?.map(user => (
                      <option key={user.id} value={user.email}>{user.full_name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-virgo-teal-500">
                    The attorney primarily responsible for handling this case
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                    Originating Attorney
                  </label>
                  <select
                    value={formData.originating_attorney}
                    onChange={(e) => handleInputChange('originating_attorney', e.target.value)}
                    className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  >
                    <option value="">— Select Attorney —</option>
                    {assignableUsers.attorneys?.map(user => (
                      <option key={user.id} value={user.email}>{user.full_name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-virgo-teal-500">
                    The attorney who brought this client/case to the firm
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                    Responsible Staff
                  </label>
                  <select
                    value={formData.responsible_staff}
                    onChange={(e) => handleInputChange('responsible_staff', e.target.value)}
                    className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  >
                    <option value="">— Select Staff —</option>
                    {assignableUsers.staff?.map(user => (
                      <option key={user.id} value={user.email}>{user.full_name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-virgo-teal-500">
                    The paralegal or staff member assigned to support this case
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Signers */}
          {step === 4 && (
            <div className="space-y-6 max-w-2xl mx-auto">
              <div className="bg-virgo-navy-800/50 rounded-lg p-4 border border-green-500/30">
                <h3 className="text-lg font-medium text-white mb-2">Signature Request</h3>
                <p className="text-sm text-virgo-teal-400">
                  The engagement letter will be sent to the client for electronic signature.
                  Add any additional signers if needed.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-virgo-teal-400">Signers</label>
                  <button
                    onClick={addSigner}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Signer
                  </button>
                </div>

                <div className="space-y-4">
                  {formData.signers.map((signer, index) => (
                    <div key={index} className="p-4 border border-virgo-teal-700/30 rounded-lg bg-virgo-navy-800/50">
                      <div className="flex items-start justify-between mb-3">
                        <span className="text-sm font-medium text-white">
                          {signer.signer_type === 'client' ? 'Client' : `Signer ${index + 1}`}
                        </span>
                        {index > 0 && (
                          <button
                            onClick={() => removeSigner(index)}
                            className="text-red-400 hover:text-red-300 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-virgo-teal-500 mb-1">Name</label>
                          <input
                            type="text"
                            value={signer.signer_name}
                            onChange={(e) => handleSignerChange(index, 'signer_name', e.target.value)}
                            className="w-full px-3 py-2 text-sm bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-virgo-teal-500 mb-1">Email</label>
                          <input
                            type="email"
                            value={signer.signer_email}
                            onChange={(e) => handleSignerChange(index, 'signer_email', e.target.value)}
                            className="w-full px-3 py-2 text-sm bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-virgo-teal-500 mb-1">Type</label>
                          <select
                            value={signer.signer_type}
                            onChange={(e) => handleSignerChange(index, 'signer_type', e.target.value)}
                            className="w-full px-3 py-2 text-sm bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                          >
                            <option value="client">Client</option>
                            <option value="witness">Witness</option>
                            <option value="other">Other</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-virgo-teal-500 mb-1">Signing Order</label>
                          <input
                            type="number"
                            min="1"
                            value={signer.signing_order}
                            onChange={(e) => handleSignerChange(index, 'signing_order', parseInt(e.target.value))}
                            className="w-full px-3 py-2 text-sm bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Email Subject</label>
                <input
                  type="text"
                  value={formData.email_subject}
                  onChange={(e) => handleInputChange('email_subject', e.target.value)}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Email Message</label>
                <textarea
                  value={formData.email_message}
                  onChange={(e) => handleInputChange('email_message', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>
            </div>
          )}

          {/* Step 5: Preview */}
          {step === 5 && (
            <div className="space-y-4">
              {/* AI Review Section */}
              {aiReview && (
                <div className={`rounded-lg p-4 border mb-4 ${
                  aiReview.status === 'approved'
                    ? 'bg-green-900/30 border-green-500/50'
                    : aiReview.status === 'needs_attention'
                    ? 'bg-yellow-900/30 border-yellow-500/50'
                    : 'bg-red-900/30 border-red-500/50'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${
                      aiReview.status === 'approved' ? 'bg-green-500/20' :
                      aiReview.status === 'needs_attention' ? 'bg-yellow-500/20' : 'bg-red-500/20'
                    }`}>
                      {aiReview.status === 'approved' ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : aiReview.status === 'needs_attention' ? (
                        <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      ) : (
                        <Bot className="w-5 h-5 text-red-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                        <Bot className="w-4 h-4 text-cyan-400" />
                        Astraea AI Review
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          aiReview.status === 'approved' ? 'bg-green-500/30 text-green-300' :
                          aiReview.status === 'needs_attention' ? 'bg-yellow-500/30 text-yellow-300' :
                          'bg-red-500/30 text-red-300'
                        }`}>
                          {aiReview.status === 'approved' ? 'Approved' :
                           aiReview.status === 'needs_attention' ? 'Needs Attention' : 'Review Error'}
                        </span>
                      </h3>
                      <p className="text-sm text-virgo-teal-300 mt-1 whitespace-pre-wrap">{aiReview.feedback}</p>
                    </div>
                  </div>
                </div>
              )}

              {previewSummary && (
                <div className="bg-virgo-navy-800/50 rounded-lg p-4 border border-green-500/30 mb-4">
                  <h3 className="text-lg font-medium text-white mb-2">Letter Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-virgo-teal-500">Client:</span>
                      <p className="text-white">{previewSummary.client_name}</p>
                    </div>
                    <div>
                      <span className="text-virgo-teal-500">Practice Area:</span>
                      <p className="text-white">{previewSummary.practice_area || 'General'}</p>
                    </div>
                    <div>
                      <span className="text-virgo-teal-500">
                        {previewSummary.fee_type === 'flat' ? 'Fee Structure:' : 'Prepaid Hours:'}
                      </span>
                      <p className="text-white">
                        {previewSummary.fee_type === 'flat'
                          ? `Flat Fee — $${previewSummary.initial_payment?.toLocaleString()} initial`
                          : `${previewSummary.prepaid_hours} hrs @ $${previewSummary.prepaid_rate}/hr`}
                      </p>
                    </div>
                    <div>
                      <span className="text-virgo-teal-500">Total Amount:</span>
                      <p className="text-green-400 font-semibold">${previewSummary.total_amount?.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ maxHeight: '50vh' }}>
                <div
                  className="p-4 overflow-y-auto"
                  style={{ maxHeight: '60vh' }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-virgo-navy-600 flex items-center justify-between bg-virgo-navy-800/50">
          <button
            onClick={() => {
              if (step === 5) {
                setStep(4);
              } else {
                setStep(step - 1);
              }
            }}
            disabled={step === 1}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
            >
              Cancel
            </button>

            {step < 4 && (
              <button
                onClick={() => setStep(step + 1)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 shadow-lg shadow-cyan-500/30 transition-all"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            )}

            {step === 4 && (
              <button
                onClick={handlePreview}
                disabled={previewLoading || !formData.scope_of_representation}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30 transition-all"
              >
                {previewLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Loading Preview...
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4" />
                    Preview Letter
                  </>
                )}
              </button>
            )}

            {step === 5 && (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-500/30 transition-all"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Generate & Send
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementLetterModal;
